import { getDownloadURL, getStorage, uploadBytes } from "firebase/storage";
import { ref as sRef } from "firebase/storage";
import attachmentIcon from "@img/attachment.svg";
import sendLogo from "@img/send.svg";
import modalSendLogo from "@img/modal-send.svg";
import { db } from "@/firebase/firebaseConfig";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { useState } from "react";
import Button from "../UI/Button";
import AutoResizingInput from "../UI/AutoResizingInput";
import fileIcon from "@img/file.svg";
import { v4 as uuidv4 } from "uuid";

const ChatInput = ({
  id,
  theme,
  message,
  selectedUser,
  adminId,
  setMessage,
  selectedImage,
  closeImageModal,
  replyTo,
  setReplyTo,
}) => {
  const [fileUrl, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [fileType, setFileType] = useState(null);

  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  const sendMessage = async () => {
    if (!id) {
      console.error("Ошибка: пользователь не авторизован.");
      return;
    }

    if (isUploading) {
      console.error("Ошибка: файл еще загружается.");
      return;
    }

    if (!message.trim() && !fileUrl) {
      console.error("Ошибка: нельзя отправить пустое сообщение без файла.");
      return;
    }

    try {
      const messageData = {
        senderId: getSenderId(),
        message: message.trim(),
        fileUrl,
        timestamp: serverTimestamp(),
        isRead: id === adminId,
        replyTo: replyTo
          ? { fileUrl: replyTo.fileUrl, message: replyTo.message }
          : null,
      };

      setMessage("");
      setReplyTo(null);
      await sendToFirestore(messageData);
      setFile(null);
      setModalOpen(false);
    } catch (e) {
      handleError("Ошибка при отправке сообщения", e);
    }
  };

  const getSenderId = () => {
    return id === adminId ? adminId : id;
  };

  const sendToFirestore = async (messageData) => {
    const messagesRef = collection(
      db,
      "chat",
      selectedUser ? selectedUser.userId : id,
      "messages",
    );
    await addDoc(messagesRef, messageData);
  };

  const handleError = (message, error) => {
    console.error(message, error);
  };

  const handleFileUpload = () => {
    document.getElementById("file-input").click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!id) {
      console.error("Ошибка: пользователь не авторизован.");
      return;
    }

    if (!file.type.startsWith("image/") && !allowedTypes.includes(file.type)) {
      alert("Ошибка: Недопустимый формат файла");
      return;
    }

    setIsUploading(true);
    setModalOpen(true);
    setPreview(URL.createObjectURL(file));
    setFileType(file.type);

    try {
      const url = await uploadFile(file);

      const newFileUrl = `${url}&timestamp=${new Date().getTime()}`;

      setFile(newFileUrl);
    } catch (error) {
      handleError("Ошибка загрузки файла", error);
      setModalOpen(false);
    } finally {
      setIsUploading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setFile(null);
    setPreview(null);
  };

  const uploadFile = async (file) => {
    const storage = getStorage();
    const uniqueFileName = `${uuidv4()}-${file.name}`; // добавляем временную метку
    const storageRef = sRef(storage, `${id}/files/${uniqueFileName}`);

    const snapshot = await uploadBytes(storageRef, file);

    return await getDownloadURL(snapshot.ref);
  };

  const handleKeyDown = (e) => {
    if (isUploading) {
      e.preventDefault();
      return;
    }
    if (e.key === "Enter") {
      if (e.shiftKey) {
        e.preventDefault();
        const textarea = e.target;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;

        textarea.value =
          textarea.value.substring(0, start) +
          "\n" +
          textarea.value.substring(end);

        textarea.selectionStart = textarea.selectionEnd = start + 1;
      } else {
        e.preventDefault();
        sendMessage();
      }
    }
  };

  const renderFile = (fileUrl) => {
    const decodedUrl = decodeURIComponent(fileUrl);
    const fileNameWithParams = decodedUrl.split("/").pop();

    const fileName = fileNameWithParams.split("?")[0];
    const fileExtension = fileName.split(".").pop().toLowerCase();

    if (["jpg", "jpeg", "png", "gif", "svg"].includes(fileExtension)) {
      return <img loading="lazy" src={fileUrl} width={25} alt="reply-img" />;
    } else {
      return (
        <div>
          <img src={fileIcon} width={15} alt="file-icon" />
          <p className="chat-body__file"> {`${fileName.slice(0, 20)}...`} </p>
        </div>
      );
    }
  };

  const renderPreview = () => {
    if (fileType && fileType.startsWith("image")) {
      return (
        <img src={preview} alt="Предпросмотр" className="file-modal__image" />
      );
    } else {
      return (
        <div className="file-modal__document">
          <span>Документ: {preview?.split("/").pop()}</span>
        </div>
      );
    }
  };

  return (
    <>
      <div className={`chat-input chat-input--${theme}`}>
        {!selectedUser && id === adminId ? (
          <input disabled type="text" className="chat-input__text" />
        ) : (
          <div className="chat-input-fields">
            {replyTo && (
              <div className="chat-input-fields__reply">
                {replyTo.fileUrl && renderFile(replyTo.fileUrl)}
                {replyTo.message && replyTo.message.length > 100
                  ? `${replyTo.message.substring(0, 100)}...`
                  : replyTo.message}

                <button
                  className="chat-input-fields__close"
                  onClick={() => setReplyTo(null)}
                >
                  x
                </button>
              </div>
            )}
            <AutoResizingInput
              handleKeyDown={handleKeyDown}
              setMessage={setMessage}
              message={message}
            />
          </div>
        )}

        <input
          onChange={handleFileChange}
          style={{ display: "none" }}
          id="file-input"
          type="file"
        />

        <Button
          image={attachmentIcon}
          onClick={handleFileUpload}
          color="blue"
        ></Button>

        <Button image={sendLogo} color="blue" onClick={sendMessage}></Button>
      </div>

      {selectedImage && (
        <div className="file-modal" onClick={closeImageModal}>
          <div
            className="image-modal__content"
            onClick={(e) => e.stopPropagation()}
          >
            <img src={selectedImage} alt="Увеличенное изображение" />
            <button onClick={closeImageModal}>Закрыть</button>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="file-modal" onClick={closeModal}>
          <div
            className={`file-modal__content file-modal__content--${theme}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={`file-modal-header__button file-modal-header__button--${theme} `}
              onClick={closeModal}
            >
              x
            </button>

            {preview && renderPreview()}

            <div className={`file-modal-input file-modal-input--${theme}`}>
              <AutoResizingInput
                handleKeyDown={handleKeyDown}
                setMessage={setMessage}
                message={message}
              />
              {isUploading ? (
                <button disabled className="file-modal__button">
                  <span className="loader--xs loader--xs--white" />
                </button>
              ) : (
                <button onClick={sendMessage} className="file-modal__button">
                  <img src={modalSendLogo} alt="modal-send-icon" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatInput;
